import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { useUser } from "@/context/UserProvider";
import { MunicipalitySwitcher } from "@/components/launch_pad/MunicipalitySwitcher";
import { LaunchPadSection } from "@/components/launch_pad/LaunchPadSection";
import { LaunchPadHero } from "@/components/launch_pad/LaunchPadHero";
import { LaunchPadQuickActions } from "@/components/launch_pad/LaunchPadQuickActions";
import { LaunchPadRecentFeed } from "@/components/launch_pad/LaunchPadRecentFeed";
import { LaunchPadToolsExplorer } from "@/components/launch_pad/LaunchPadToolsExplorer";
import { LaunchPadInsightsPanel } from "@/components/launch_pad/LaunchPadInsightsPanel";
import { LaunchPadSupportSection } from "@/components/launch_pad/LaunchPadSupportSection";
import { useLaunchPadDashboard } from "@/components/launch_pad/useLaunchPadDashboard";
import { formatMunicipalityLabel } from "@/lib/municipalityLabel";
import type { ActiveSection, LaunchPadNavigateOptions } from "@/types/appNavigation";

export type LaunchPadProps = {
  onNavigate: (section: ActiveSection, options?: LaunchPadNavigateOptions) => void;
};

export function LaunchPad({ onNavigate }: LaunchPadProps) {
  const { currentUser } = useUser();
  const {
    municipalityId,
    municipality,
    isLoading,
    impactStats,
    impactLoading,
    linksLoading,
    fileLibraryLoading,
    recentFeed,
    activeResourceLinks,
    fileLibrarySummary,
  } = useLaunchPadDashboard();

  if (!currentUser) {
    return null;
  }

  const municipalityLabel = municipality ? formatMunicipalityLabel(municipality) : undefined;

  const showSidebar = Boolean(municipalityId);

  return (
    <div className="flex h-full min-h-0 flex-col bg-background" data-testid="launch-pad">
      <header className="shrink-0 border-b px-4 py-4 sm:px-6">
        <div className="mx-auto flex max-w-6xl justify-end">
          <MunicipalitySwitcher />
        </div>
      </header>

      <ScrollArea className="flex-1">
        <div className="mx-auto max-w-6xl space-y-8 px-4 py-6 sm:px-6 sm:py-8">
          <LaunchPadHero
            userEmail={currentUser.email}
            municipalityName={municipalityLabel}
            impactStats={impactStats}
            impactLoading={impactLoading}
          />

          <LaunchPadQuickActions user={currentUser} onNavigate={onNavigate} />

          <div className={showSidebar ? "grid gap-8 xl:grid-cols-[1fr_300px] xl:items-start" : undefined}>
            <div className="min-w-0 space-y-8">
              {recentFeed.length > 0 && (
                <LaunchPadSection
                  title="Pick up where you left off"
                  headingId="resume-heading"
                  showSeparator={false}
                >
                  <LaunchPadRecentFeed items={recentFeed} onNavigate={onNavigate} />
                </LaunchPadSection>
              )}

              {municipalityId && isLoading && recentFeed.length === 0 && (
                <div className="space-y-2">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <Skeleton key={i} className="h-14 w-full rounded-lg" />
                  ))}
                </div>
              )}

              <LaunchPadSection
                title="Tools"
                description="Open any product area."
                headingId="tools-heading"
                showSeparator={recentFeed.length > 0}
              >
                <LaunchPadToolsExplorer user={currentUser} onSelect={onNavigate} />
              </LaunchPadSection>
            </div>

            {showSidebar && (
              <aside className="min-w-0 xl:sticky xl:top-0">
                <LaunchPadInsightsPanel
                  municipalityId={municipalityId}
                  municipalityName={municipalityLabel}
                  showCaseLookup={Boolean(currentUser.case_portal_access)}
                  showResourceLinks={Boolean(currentUser.municipality_resource_links_access)}
                  showFileCenter={Boolean(currentUser.file_viewer)}
                  resourceLinks={activeResourceLinks}
                  linksLoading={linksLoading}
                  fileLibrarySummary={fileLibrarySummary}
                  fileLibraryLoading={fileLibraryLoading}
                  onNavigate={onNavigate}
                />
              </aside>
            )}
          </div>

          <LaunchPadSupportSection
            municipalityId={municipalityId}
            municipalityName={municipalityLabel}
          />
        </div>
      </ScrollArea>
    </div>
  );
}
